import {
  applyActionCode,
  ActionCodeSettings,
  checkActionCode,
  confirmPasswordReset,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  updateProfile,
} from 'firebase/auth';
import { FirebaseError } from 'firebase/app';
import { CustomerInfo } from '../models/customer.interface';
import { getFirebaseAuth } from '../firebase/auth';
import { apiAuthRoutes, baseRoutes } from '../constants/routes.constants';
import apiClient from '../api/client';
import { saveOwnProfile } from '../api/account/profile';
import passwordSchema from '../utils/validators';

// What a successful auth flow gives the shared form (useForm): a truthy `data`, which is what makes it redirect.
// Nothing else rides on it: the store is written by FirebaseAuthSessionBridge alone (see signInWithFirebase).
type AuthFlowResult = {
  data: { ok: true };
};

const AUTH_OK: AuthFlowResult = { data: { ok: true } };

const AUTH_SESSION_ENDPOINT =
  process.env.REACT_APP_AUTH_SESSION_ENDPOINT || apiAuthRoutes.firebaseSession;

const syncBackendSession = async (idToken: string): Promise<void> => {
  // skipAuthRetry: the sign-in call itself never starts the "access changed" handler (api/client.ts).
  await apiClient.post(AUTH_SESSION_ENDPOINT, { idToken }, { skipAuthRetry: true });
};

const getCustomerInfo = async (): Promise<CustomerInfo> => {
  const response = await apiClient.get<CustomerInfo>('/customer');
  return response.data;
};

/**
 * Where the links in Firebase's emails send the person back to: this site's own sign-in page, on whichever host they
 * are using (dev, UAT, beta.thecodeword.ai...), so nothing per environment is configured here. The host must be on
 * the Identity Platform authorised domains list, or Firebase refuses to send (auth/unauthorized-continue-uri).
 */
const authActionCodeSettings = (): ActionCodeSettings => ({
  url: `${window.location.origin}${baseRoutes.signIn}`,
});

const signInWithFirebase = async (email: string, password: string): Promise<AuthFlowResult> => {
  const auth = getFirebaseAuth();
  await signInWithEmailAndPassword(auth, email, password);

  // Session sync (POST /auth/firebase/session) and the customer fetch (GET /customer)
  // are NOT done here - signInWithEmailAndPassword's own auth-state change is what
  // fires FirebaseAuthSessionBridge's onIdTokenChanged listener, which already does
  // both. Doing them here too meant every login fired each call twice (the reported
  // "double login") with two independent, uncoordinated Redux dispatches racing.
  return AUTH_OK;
};

interface SignUpDetails {
  firstName: string;
  lastName: string;
  companyName: string;
  email: string;
  password: string;
}

/**
 * Open sign-up (27 Oct, the sign-in build, step 6). The account is created with Firebase; it has no role and no
 * tenant until a Platform Admin grants one, so the person lands on the waiting page.
 *
 * Only the account creation can fail the sign-up. After it, the account exists, so nothing else is allowed to look like
 * a failed sign-up:
 *   1. The display name ("first last"): the fallback the buyer grant uses if the profile never arrives.
 *   2. The profile (names and company) to the gateway, tried three times (api/account/profile.ts). If it still fails it
 *      is kept in memory for the waiting page's "Save my details".
 *   3. The confirmation email, with the link back to this site. If it fails, the waiting page's "Resend email" sends it.
 * The session sync and the store are FirebaseAuthSessionBridge's, as for sign-in.
 */
const signUpWithFirebase = async (details: SignUpDetails): Promise<AuthFlowResult> => {
  assertPasswordRule(details.password);
  const auth = getFirebaseAuth();
  const credential = await createUserWithEmailAndPassword(auth, details.email, details.password);
  const { user } = credential;

  const fullName = `${details.firstName.trim()} ${details.lastName.trim()}`.trim();
  try {
    if (fullName) await updateProfile(user, { displayName: fullName });
  } catch (error) {
    console.error('Sign-up: the display name was not set');
  }

  try {
    await saveOwnProfile({
      firstName: details.firstName,
      lastName: details.lastName,
      companyName: details.companyName,
    });
  } catch (error) {
    console.error('Sign-up: the profile was not saved; the waiting page offers to save it');
  }

  try {
    await sendEmailVerification(user, authActionCodeSettings());
  } catch (error) {
    console.error('Sign-up: the confirmation email was not sent; the waiting page offers to resend it');
  }

  return AUTH_OK;
};

// The two answers that say something about the ACCOUNT behind an address. Forgot password never passes them on.
const ACCOUNT_REVEALING_CODES = ['auth/user-not-found', 'auth/user-disabled'];

/**
 * Forgot password. Firebase's answer is not passed on when it is about the account (no account, a disabled one; with
 * email enumeration protection on, Firebase does not say either), so the page shows the same sentence as for a sent
 * link. Every other refusal is about the request or the set-up (an invalid address, too many tries, no network, a host
 * missing from the authorised domains) and reaches the caller, so the page does not claim a link was sent when none
 * was. Those are logged by their code only, never with the address.
 */
const sendFirebasePasswordReset = async (email: string): Promise<void> => {
  const auth = getFirebaseAuth();
  try {
    await sendPasswordResetEmail(auth, email, authActionCodeSettings());
  } catch (error) {
    if (error instanceof FirebaseError && ACCOUNT_REVEALING_CODES.includes(error.code)) {
      return;
    }
    console.error(`Forgot password: the reset email was refused (${error instanceof FirebaseError ? error.code : 'not a Firebase error'})`);
    throw error;
  }
};

/**
 * The existing password rule (utils/validators.ts), checked again before Firebase is asked: the field-by-field check in
 * the shared Input runs only once a field has been focused, so a password filled without focus (autofill) would
 * otherwise go through unchecked. Fails closed with Firebase's own weak-password code.
 */
const assertPasswordRule = (password: string): void => {
  if (!passwordSchema.validate(password)) {
    throw new FirebaseError('auth/weak-password', 'The password does not meet the rule.');
  }
};

/**
 * The reset link: check the code, then set the new password. No automatic sign-in: the person signs in with the new
 * password, which also starts a fresh gateway session. The code must be a password-reset code (item 7's security review,
 * 28 Oct): Firebase's verifyPasswordResetCode does not check the kind, so a code for another action is refused here.
 */
const resetPasswordWithFirebase = async (oobCode: string, newPassword: string): Promise<void> => {
  assertPasswordRule(newPassword);
  const auth = getFirebaseAuth();
  const info = await checkActionCode(auth, oobCode);
  if (info.operation !== 'PASSWORD_RESET') {
    throw new FirebaseError('auth/invalid-action-code', 'Not a password-reset link.');
  }
  await confirmPasswordReset(auth, oobCode, newPassword);
};

// How long Resend waits before it may send again (27 Oct, step 7). Firebase limits these emails too; this keeps a
// button from being hammered. Kept for the page's life, so leaving and re-opening the waiting page does not reset it.
const RESEND_COOLDOWN_MS = 60_000;
let lastResendAt = 0;

/** Milliseconds until Resend may send again; 0 when it may. */
const resendWaitMs = (now: number = Date.now()): number => Math.max(0, lastResendAt + RESEND_COOLDOWN_MS - now);

/**
 * Send the confirmation email again, to the signed-in account's own address, with the link back to this site. Refused
 * (auth/too-many-requests) while the cooldown runs. No signed-in account: nothing is sent. Returns the address it was sent
 * to (Firebase's, never the store's), for the message.
 */
const resendConfirmationEmail = async (): Promise<string> => {
  const user = getFirebaseAuth().currentUser;
  if (!user) throw new FirebaseError('auth/no-current-user', 'Not signed in.');
  if (resendWaitMs() > 0) throw new FirebaseError('auth/too-many-requests', 'Wait before sending again.');
  lastResendAt = Date.now();
  await sendEmailVerification(user, authActionCodeSettings());
  return user.email ?? '';
};

/**
 * "I've confirmed it": read the account again from Firebase and force a new token, so the confirmed address shows
 * (the session bridge picks the new token up). Returns whether Firebase now says the address is confirmed.
 */
const refreshEmailVerified = async (): Promise<boolean> => {
  const user = getFirebaseAuth().currentUser;
  if (!user) return false;
  await user.reload();
  await user.getIdToken(true);
  return getFirebaseAuth().currentUser?.emailVerified === true;
};

/** The signed-in account's own display name ("First Last", set at sign-up), or '' (item 5, "My information"). */
const getOwnDisplayName = (): string => getFirebaseAuth().currentUser?.displayName ?? '';

/** Keep the signed-in account's own display name in step with a saved profile (item 5). Nobody signed in: nothing. */
const setOwnDisplayName = async (displayName: string): Promise<void> => {
  const user = getFirebaseAuth().currentUser;
  if (!user) return;
  await updateProfile(user, { displayName });
};

/**
 * Change password (step 7b): Firebase's reset email to the SIGNED-IN account's own address, taken from Firebase, never
 * from anything typed or passed in. Returns the address it was sent to, for the "check your email" message.
 */
const sendOwnPasswordReset = async (): Promise<string> => {
  const email = getFirebaseAuth().currentUser?.email;
  if (!email) throw new FirebaseError('auth/no-current-user', 'Not signed in.');
  await sendPasswordResetEmail(getFirebaseAuth(), email, authActionCodeSettings());
  return email;
};

/** Tests only: forget the last Resend. */
const __resetResendForTests = () => {
  lastResendAt = 0;
};

/**
 * The confirm-email link, step one: read the code without using it, and say which address it confirms. Opening the link
 * (a person, or a mail scanner that follows links) confirms nothing; only confirmEmailWithFirebase, on a click, does.
 */
const checkConfirmEmailCode = async (oobCode: string): Promise<string> => {
  const info = await checkActionCode(getFirebaseAuth(), oobCode);
  if (info.operation !== 'VERIFY_EMAIL' || !info.data.email) {
    throw new FirebaseError('auth/invalid-action-code', 'Not a confirm-email link.');
  }
  return info.data.email;
};

/** The confirm-email link, step two, on the person's click. */
const confirmEmailWithFirebase = async (oobCode: string): Promise<void> => {
  await applyActionCode(getFirebaseAuth(), oobCode);
};

/**
 * The undo-email-change link (mode recoverEmail, item 7), step one: read the code without using it. Returns the address
 * the account goes back to and the one it has now, both from Firebase (never from the link). A code for another action,
 * or one that names no address, is an invalid link.
 */
const checkRecoverEmailCode = async (oobCode: string): Promise<{ restoredEmail: string; currentEmail: string }> => {
  const info = await checkActionCode(getFirebaseAuth(), oobCode);
  if (info.operation !== 'RECOVER_EMAIL' || !info.data.email) {
    throw new FirebaseError('auth/invalid-action-code', 'Not an undo-email-change link.');
  }
  return { restoredEmail: info.data.email, currentEmail: info.data.previousEmail ?? '' };
};

/**
 * The verify-and-change-email link (mode verifyAndChangeEmail, item 7), step one: read the code without using it.
 * Returns the new address, from Firebase. Not the one it replaces: this link goes to the NEW address, so a mistyped
 * new address would show a stranger the account's current one (item 7's security review, 28 Oct).
 */
const checkChangeEmailCode = async (oobCode: string): Promise<{ newEmail: string }> => {
  const info = await checkActionCode(getFirebaseAuth(), oobCode);
  if (info.operation !== 'VERIFY_AND_CHANGE_EMAIL' || !info.data.email) {
    throw new FirebaseError('auth/invalid-action-code', 'Not a change-email link.');
  }
  return { newEmail: info.data.email };
};

/** Step two of the undo and change links, on the person's click (the same Firebase call as confirm email). */
const applyEmailActionCode = async (oobCode: string): Promise<void> => {
  await applyActionCode(getFirebaseAuth(), oobCode);
};

/**
 * After an email change is undone: Firebase's reset email to the RESTORED address, which the undo link's own check
 * returned (Firebase's data, never anything typed or taken from the link), on the person's click.
 */
const sendPasswordResetToRestoredEmail = async (restoredEmail: string): Promise<void> => {
  await sendPasswordResetEmail(getFirebaseAuth(), restoredEmail, authActionCodeSettings());
};

// A failure that says nothing about the link itself (item 7's security review, 28 Oct): the network, Firebase's own
// fault, its rate limit, or anything that is not an auth error at all. Everything else (a bad, expired, used or wrong-kind
// code, a disabled account, an address taken) is a refusal.
const TRANSIENT_AUTH_CODES = new Set(['auth/network-request-failed', 'auth/internal-error', 'auth/too-many-requests', 'auth/timeout']);
const isTransientFailure = (error: unknown): boolean => {
  const code = (error as { code?: unknown } | null)?.code;
  return typeof code !== 'string' || !code.startsWith('auth/') || TRANSIENT_AUTH_CODES.has(code);
};

const isFirebaseError = (error: unknown): error is FirebaseError => {
  return error instanceof FirebaseError;
};

export {
  __resetResendForTests,
  authActionCodeSettings,
  checkConfirmEmailCode,
  confirmEmailWithFirebase,
  checkRecoverEmailCode,
  checkChangeEmailCode,
  applyEmailActionCode,
  sendPasswordResetToRestoredEmail,
  refreshEmailVerified,
  resendConfirmationEmail,
  resendWaitMs,
  sendOwnPasswordReset,
  getOwnDisplayName,
  setOwnDisplayName,
  getCustomerInfo,
  isFirebaseError,
  isTransientFailure,
  sendFirebasePasswordReset,
  signInWithFirebase,
  signUpWithFirebase,
  syncBackendSession,
  resetPasswordWithFirebase,
};
export type { SignUpDetails };
